import { nextTick } from "vue";
import type { RouteLocationRaw } from "vue-router";

import { PROVIDER_TO_MODELS_ORDERED } from "~/constants/model";
import { AiModel, FolderSort, type AiProvider } from "~/shared/enums";
import type { Comparator, Doc, EntityType } from "~/shared/types";
import { makeDocComparator, makeStringComparator } from "~/utils/comparator";
import { printOrCaptureMessage } from "~/utils/exceptionClient";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";

export type State = {
  _entityDuidsBlacklist: Set<string>;
  _entityDuidsPendingCreation: Set<string>;
  _entityDuidsPendingDeletion: Set<string>;
  pendingNavigationDuids: Set<string>;
};

export const state: State = {
  _entityDuidsBlacklist: new Set(),
  _entityDuidsPendingCreation: new Set(),
  _entityDuidsPendingDeletion: new Set(),
  pendingNavigationDuids: new Set(),
};

export type Getters = {
  $getDocSortingFn: (order: FolderSort) => Comparator<Doc | undefined>;
  $isEntityDuidBlacklisted: (duid: string) => boolean;
  $isEntityDuidPendingCreation: (duid: string) => boolean;
  $isEntityDuidPendingDeletion: (duid: string) => boolean;
};

export type Actions = {
  $blacklistEntityDuid: (entity: EntityType, duid: string) => void;
  $unblacklistEntityDuid: (duid: string) => void;
  $addEntityDuidPendingCreation: (duid: string) => void;
  $removeEntityDuidPendingCreation: (duid: string) => void;
  $addEntityDuidPendingDeletion: (duid: string) => void;
  $removeEntityDuidPendingDeletion: (duid: string) => void;
  $addPendingNavigationDuid: (duid: string) => void;
  $navigateToPendingDuid: (duid: string, to: RouteLocationRaw) => Promise<void>;
  $resetAiModelsForProviderNoBackend: (provider: AiProvider) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  $getDocSortingFn() {
    return (order) => {
      const orderToSortFn: Record<FolderSort, Comparator> = {
        [FolderSort.RECENT_ASC]: makeStringComparator((e: Doc) => e.updatedAt, false),
        [FolderSort.RECENT_DESC]: makeStringComparator((e: Doc) => e.updatedAt, true),
        [FolderSort.CREATED_ASC]: makeStringComparator((e: Doc) => e.createdAt, false),
        [FolderSort.CREATED_DESC]: makeStringComparator((e: Doc) => e.createdAt, true),
        [FolderSort.TITLE_ASC]: makeStringComparator((e: Doc) => e.title.toLowerCase(), false),
        [FolderSort.TITLE_DESC]: makeStringComparator((e: Doc) => e.title.toLowerCase(), true),
        [FolderSort.ORDER]: makeDocComparator(this.getSpaceByDuid, this.getFolderByDuid),
      };
      return orderToSortFn[order] || orderToSortFn[FolderSort.ORDER];
    };
  },
  $isEntityDuidBlacklisted() {
    return (duid: string) => this._entityDuidsBlacklist.has(duid);
  },
  $isEntityDuidPendingCreation() {
    return (duid: string) => this._entityDuidsPendingCreation.has(duid);
  },
  $isEntityDuidPendingDeletion() {
    return (duid: string) => this._entityDuidsPendingDeletion.has(duid);
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  $blacklistEntityDuid(entity, duid) {
    this._entityDuidsBlacklist.add(duid);
    printOrCaptureMessage("Blacklisted entity", { entity, duid });
  },
  $unblacklistEntityDuid(duid) {
    this._entityDuidsBlacklist.delete(duid);
  },
  $addEntityDuidPendingCreation(duid) {
    this._entityDuidsPendingCreation.add(duid);
  },
  $removeEntityDuidPendingCreation(duid) {
    this._entityDuidsPendingCreation.delete(duid);
  },
  $addEntityDuidPendingDeletion(duid) {
    this._entityDuidsPendingDeletion.add(duid);
  },
  $removeEntityDuidPendingDeletion(duid) {
    this._entityDuidsPendingDeletion.delete(duid);
  },
  $addPendingNavigationDuid(duid) {
    this.pendingNavigationDuids.add(duid);
  },
  async $navigateToPendingDuid(duid, to) {
    try {
      await this.$router.push(to);
    } finally {
      await nextTick();
      this.pendingNavigationDuids.delete(duid);
    }
  },
  $resetAiModelsForProviderNoBackend(provider) {
    const providerModels = new Set<AiModel>(PROVIDER_TO_MODELS_ORDERED[provider]);

    this._duidsToChats.forEach((chat) => {
      if (!providerModels.has(chat.model)) {
        return;
      }
      this.updateChat({ duid: chat.duid, model: AiModel.AUTO }, { noBackend: true });
    });
    this._duidsToSkills.forEach((skill) => {
      if (!providerModels.has(skill.aiModel)) {
        return;
      }
      this.updateSkill({ duid: skill.duid, aiModel: AiModel.AUTO }, { noBackend: true });
    });
    this._duidsToUsers.forEach((user) => {
      if (!user.agent || !providerModels.has(user.agent.model)) {
        return;
      }
      this.updateAgent({ duid: user.duid, agent: { model: AiModel.AUTO } }, { noBackend: true });
    });

    const userStore = this.$useUserStore();
    if (providerModels.has(userStore.aiModel)) {
      userStore.aiModel = AiModel.AUTO;
    }
  },
};

export { actions, getters };
